import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import React, { Component, createRef } from "react";
import { FormattedMessage, type IntlShape, injectIntl, type MessageDescriptor } from "react-intl";
import { type ConnectedProps, connect } from "react-redux";
import { NavLink, type RouteComponentProps } from "react-router-dom";

import { eraseStateProp } from "~/actions/dashboard";
import { changeAccountData } from "~/actions/edit_doctor";
import { addUserActionNotification } from "~/actions/user_notification";
import { isNone, isNotNoneString } from "~/common/assert";
import { remoteLog } from "~/common/logging";
import { validEmail } from "~/common/validations/clinic";
import { Button } from "~/components/ui/button";
import { LabelledCheckbox } from "~/components/ui/labelled-checkbox";
import { Layout } from "~/components/ui/layout";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { selectUser } from "~/slices/user";
import type { RootState } from "~/store";
import type { TUser } from "~/types/user";

const MAX_STAGES = 99;

const mapStateToProps = (state: RootState) => {
  return {
    user: selectUser(state),
    doctor: state.doctor,
    clinic: state.clinic,
  };
};

const mapDispatchToProps = {
  changeAccountData,
  eraseStateProp,
  addUserActionNotification,
};

type DoctorUpdatePageProps = PropsFromRedux & { intl: IntlShape } & RouteComponentProps;

type DoctorUpdatePageState = {
  preferences: Partial<TUser["preferences"]>;
  errors: {
    telegram_id?: string;
    email?: string;
  };
  isFioPopupOpen: boolean;
};

export type TUpdatedDoctor = {
  telegram_notify?: TUser["telegram_notify"];
  telegram_id?: TUser["telegram_id"];
  email?: TUser["email"];
  email_notify?: TUser["email_notify"];
  phone?: TUser["phone"];
  // Preferences are part of the edit-doctor payload (see addPropsToUpdateDoctor("preferences", …)
  // and the redesign clinical-preferences form); typed here so callers stay type-safe.
  preferences?: Partial<TUser["preferences"]>;
};

class DoctorUpdatePage extends Component<DoctorUpdatePageProps, DoctorUpdatePageState> {
  updated_doctor: TUpdatedDoctor;
  formRef = createRef<HTMLFormElement>();
  telegramIdRef = createRef<HTMLInputElement>();
  email = createRef<HTMLInputElement>();

  constructor(props: DoctorUpdatePageProps) {
    super(props);
    this.updated_doctor = {};

    this.state = {
      preferences: props.user.preferences || {},
      errors: {},
      isFioPopupOpen: false,
    };

    this.updatePreferences = this.updatePreferences.bind(this);
    this.updatePreferenceRadio = this.updatePreferenceRadio.bind(this);
    this.handleFioFieldClick = this.handleFioFieldClick.bind(this);
    this.closeFioPopup = this.closeFioPopup.bind(this);
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "doctor_update_page");
  }

  componentDidMount() {
    const { user } = this.props;
    this.props.eraseStateProp("doctor");

    _paq.push(["HeatmapSessionRecording::enable"]);
    if (user && user.account_id) {
      _paq.push(["setUserId", user.account_id.toString()]);
    }
  }

  componentDidUpdate(prevProps: DoctorUpdatePageProps) {
    const { user, doctor } = this.props;
    if (user.preferences !== prevProps.user.preferences) {
      this.setState({ preferences: { ...user.preferences } });
    }
    if (doctor && doctor.account_id) {
      this.props.history.push("/pages/my_account");
    }
  }

  addPropsToUpdateDoctor(field_name: keyof TUpdatedDoctor, data: string) {
    this.updated_doctor[field_name] = data;
  }

  updatePreferences(e) {
    const { target } = e;
    let { value } = target;
    const key = target.name.split(".")[1];

    if (target.type === "checkbox") value = target.checked;
    if (target.type === "number" || $(target).data("type") === "number") {
      if (value === null || value === undefined || value === "") {
        value = null;
      } else {
        value = +value;
      }
    }

    const preferences = { ...this.state.preferences, [key]: value };

    this.setState({ preferences });
    this.addPropsToUpdateDoctor("preferences", preferences);
  }

  updatePreferenceRadio(e) {
    const { target } = e;
    const key = target.name.split(".")[1];
    let value = target.value ? +target.value : null;
    const oldValue = this.state.preferences[key];

    if (value == oldValue || (value !== null && oldValue !== null && Boolean(parseInt(value)) === Boolean(oldValue))) {
      value = null;
      target.checked = false;
    }
    const preferences = { ...this.state.preferences, [key]: value };
    this.setState({ preferences });
    this.addPropsToUpdateDoctor("preferences", preferences);
  }

  handleFioFieldClick(e?: React.MouseEvent | React.FocusEvent) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    this.setState({ isFioPopupOpen: true });
  }

  closeFioPopup() {
    this.setState({ isFioPopupOpen: false });
  }

  async submitButton() {
    let hasErrors = false;
    const form = this.formRef.current;
    if (!form) return;

    if (!form.checkValidity()) {
      $(form)
        .find("input[required]")
        .each((i, el) => {
          if (el.type === "checkbox" && el.checked) return;
          if (el.value) return;
          const $group = $(el).closest(".form-group").addClass("has-error");
          $(el).one("focus", () => {
            $group.removeClass("has-error");
          });
        });

      $(".floatPositive").each((i, el) => {
        const value = el.value;
        if (!value || value == "" || value == undefined) return;
        if (parseFloat(value) >= 1.0) return;
        const $groupFloatPositive = $(el).closest(".form-group").addClass("has-error");
        $(el).one("focus", () => {
          $groupFloatPositive.removeClass("has-error");
        });
      });

      $(".intPositive").each((i, el) => {
        const value = el.value;
        if (!value || value == "" || value == undefined) return;
        if (parseInt(value) >= 1 && parseInt(value) <= MAX_STAGES) return;
        const $groupIntPositive = $(el).closest(".form-group").addClass("has-error");
        $(el).one("focus", () => {
          $groupIntPositive.removeClass("has-error");
        });
      });

      hasErrors = true;
    }

    const { telegram_notify = this.props.user.telegram_notify, telegram_id = this.props.user.telegram_id, email = this.props.user.email } = this.updated_doctor;

    if (telegram_notify && !telegram_id) {
      this.setState((prevState) => ({
        errors: {
          ...prevState.errors,
          telegram_id: this.props.intl.formatMessage({ id: "telegram_id.required" }),
        },
      }));
      this.telegramIdRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
      this.telegramIdRef.current?.focus({ preventScroll: true });
      hasErrors = true;
    } else if (this.state.errors.telegram_id) {
      this.setState((prevState) => ({
        errors: {
          ...prevState.errors,
          telegram_id: undefined,
        },
      }));
    }
    const isEmailInvalid = email ? validEmail(email) : true;

    if (isEmailInvalid) {
      this.setState((prevState) => ({
        errors: {
          ...prevState.errors,
          email: this.props.intl.formatMessage({ id: "email.incorrect" }),
        },
      }));
      this.email.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
      this.email.current?.focus({ preventScroll: true });
      hasErrors = true;
    } else if (this.state.errors.email) {
      this.setState((prevState) => ({
        errors: {
          ...prevState.errors,
          email: undefined,
        },
      }));
    }

    if (!hasErrors) {
      this.props.changeAccountData(this.updated_doctor);
    }
  }

  render() {
    const { user } = this.props;
    const { errors } = this.state;

    if (!user.login) {
      return <div>loading...</div>;
    }

    return (
      <Layout>
        <Portlet as="main" testId="profile-edit-page">
          <PortletTitle iconClassName="icon-user">
            <FormattedMessage id="top.menu.profile" />
          </PortletTitle>

          <form className="portlet-body form" ref={this.formRef} data-testid="profile-edit-form">
            <h3>
              <FormattedMessage id="doctor.profile.info" />
            </h3>
            <div className="form-body">
              <div className="alert alert-danger display-hide">
                <button type="button" className="close" data-close="alert" />
                <span id="error-msg" />
              </div>
              <div className="form-group row">
                <div className="col-md-4">
                  <label className="control-label" htmlFor="login">
                    <FormattedMessage id="login.login" />
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="login"
                    name="login"
                    required
                    disabled={true}
                    defaultValue={user.login}
                    onChange={(e) => this.addPropsToUpdateDoctor("login", e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Button asChild>
                  <NavLink to="/pages/account/password/update" data-testid="profile-edit-change-password-link">
                    <FormattedMessage id="BTN_CHANGE_PASSWORD" />
                  </NavLink>
                </Button>
                <br />
                <br />
              </div>
              <div className="row">
                <div className="form-group col-md-4">
                  <label className="control-label" htmlFor="last_name">
                    <FormattedMessage id="PAT_INFO_LAST_NAME" />
                  </label>
                  <div onClick={this.handleFioFieldClick} onMouseDown={this.handleFioFieldClick} style={{ cursor: "pointer" }}>
                    <input
                      type="text"
                      className="form-control"
                      id="last_name"
                      name="last_name"
                      required
                      style={{ backgroundColor: "#f5f5f5", cursor: "pointer" }}
                      defaultValue={user.last_name}
                      readOnly
                      tabIndex={-1}
                      onFocus={() => this.handleFioFieldClick()}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        this.handleFioFieldClick(e);
                      }}
                    />
                  </div>
                </div>
                <div className="form-group col-md-4">
                  <label className="control-label" htmlFor="first_name">
                    <FormattedMessage id="PAT_INFO_FIRST_NAME" />
                  </label>
                  <div onClick={this.handleFioFieldClick} onMouseDown={this.handleFioFieldClick} style={{ cursor: "pointer" }}>
                    <input
                      type="text"
                      className="form-control"
                      id="first_name"
                      name="first_name"
                      required
                      style={{ backgroundColor: "#f5f5f5", cursor: "pointer" }}
                      defaultValue={user.first_name}
                      readOnly
                      tabIndex={-1}
                      onFocus={() => this.handleFioFieldClick()}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        this.handleFioFieldClick(e);
                      }}
                    />
                  </div>
                </div>
                <div className="form-group col-md-4">
                  <label className="control-label" htmlFor="middle_name">
                    <FormattedMessage id="PAT_INFO_MIDDLE_NAME" />
                  </label>
                  <div onClick={this.handleFioFieldClick} onMouseDown={this.handleFioFieldClick} style={{ cursor: "pointer" }}>
                    <input
                      type="text"
                      className="form-control"
                      id="middle_name"
                      name="middle_name"
                      style={{ backgroundColor: "#f5f5f5", cursor: "pointer" }}
                      defaultValue={isNotNoneString(user.middle_name) ? user.middle_name : null}
                      readOnly
                      tabIndex={-1}
                      onFocus={() => this.handleFioFieldClick()}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        this.handleFioFieldClick(e);
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="row" style={{ marginTop: "25px" }}>
                <div className="form-group col-md-4">
                  <label className={clsx("control-label", { "tw-text-red-500": errors.email })} htmlFor="email">
                    Email
                  </label>
                  <input
                    ref={this.email}
                    type="text"
                    className={clsx("form-control", { "tw-border-red-500": errors.email })}
                    id="email"
                    name="email"
                    data-testid="profile-edit-email-input"
                    defaultValue={user.email}
                    onChange={(e) => this.addPropsToUpdateDoctor("email", e.target.value.trim())}
                  />
                  {errors.email ? (
                    <p data-testid="profile-edit-email-error" className="tw-mb-0 tw-mt-[5px] tw-text-red-500">
                      {errors.email}
                    </p>
                  ) : null}
                </div>
                <div className="form-group col-md-4">
                  <label className="control-label" htmlFor="phone_number">
                    <FormattedMessage id="clin_table.phone" />
                  </label>
                  <input
                    type="text"
                    maxLength={40}
                    className="form-control"
                    id="phone_number"
                    name="phone_number"
                    required
                    defaultValue={user.phone}
                    onChange={(e) => this.addPropsToUpdateDoctor("phone", e.target.value)}
                  />
                </div>
                {
                  <div className="form-group col-md-4">
                    <label className={clsx("control-label", { "tw-text-red-500": errors.telegram_id })} htmlFor="telegram_id">
                      Telegram ID
                    </label>
                    <input
                      ref={this.telegramIdRef}
                      type="text"
                      className={clsx("form-control", { "tw-border-red-500": errors.telegram_id })}
                      id="telegram_id"
                      name="telegram_id"
                      data-testid="profile-edit-telegram-id-input"
                      defaultValue={user.telegram_id}
                      onChange={(e) => this.addPropsToUpdateDoctor("telegram_id", e.target.value.trim())}
                    />
                    {errors.telegram_id ? (
                      <p data-testid="profile-edit-telegram-id-error" className="tw-mb-0 tw-mt-[5px] tw-text-red-500">
                        {errors.telegram_id}
                      </p>
                    ) : null}
                  </div>
                }
              </div>
              <div className="form-group" style={{ marginTop: "25px", display: "flex", flexDirection: "column", gap: 8 }}>
                <p className="control-label" style={{ margin: 0 }}>
                  <FormattedMessage id="notify.doctor.subscribe.edit" />
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div className="row">
                    <div className="сol-lg-4 col-md-4 col-sm-4 col-xs-4" style={{ marginBottom: 0 }}>
                      <LabelledCheckbox
                        label="Telegram"
                        defaultValue={0}
                        defaultChecked={isNone(user.telegram_notify) ? 0 : user.telegram_notify}
                        data-testid="profile-edit-telegram-notify-checkbox"
                        onClick={(e) => this.addPropsToUpdateDoctor("telegram_notify", Number(e.target.checked))}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="offset-md-4 col-lg-4 col-md-4 col-sm-4 col-xs-4" style={{ marginBottom: 0 }}>
                      <LabelledCheckbox
                        label="Email"
                        defaultValue={0}
                        defaultChecked={isNone(user.email_notify) ? 0 : user.email_notify}
                        data-testid="profile-edit-email-notify-checkbox"
                        onClick={(e) => this.addPropsToUpdateDoctor("email_notify", Number(e.target.checked))}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <hr style={{ margin: 0 }} />
            {this.renderPrefs()}

            <div className="form-actions">
              <Button type="button" variant="primary" data-testid="profile-edit-submit" onClick={() => this.submitButton()}>
                <FormattedMessage id="BTN_SUBMIT" />
              </Button>
            </div>
          </form>

          {this.state.isFioPopupOpen ? (
            <Dialog.Root
              open={this.state.isFioPopupOpen}
              onOpenChange={(isOpen) => {
                if (!isOpen) {
                  this.closeFioPopup();
                }
              }}
            >
              <Dialog.Portal>
                <Dialog.Overlay className="tw-fixed tw-inset-0 tw-z-[100] tw-bg-black/30 tw-backdrop-blur-sm" />
                <Dialog.Content
                  className={clsx(
                    "tw-fixed tw-left-1/2 tw-top-1/2 tw-z-[100] -tw-translate-x-1/2 -tw-translate-y-1/2",
                    "tw-w-full tw-max-w-md tw-rounded-lg tw-border tw-bg-white tw-p-6 tw-shadow-lg",
                  )}
                >
                  <Dialog.Title className="tw-m-0 tw-text-xl tw-font-semibold">
                    <FormattedMessage id="doctor.fio.popup.title" />
                  </Dialog.Title>
                  <Dialog.Description className="tw-mt-4 tw-leading-relaxed">
                    <FormattedMessage
                      id="doctor.fio.popup.message"
                      values={{
                        email: (
                          <a href="mailto:info@3d-smile.ru" className="tw-text-blue-600 hover:tw-underline">
                            info@3d-smile.ru
                          </a>
                        ),
                      }}
                    />
                  </Dialog.Description>
                  <div className="tw-mt-6 tw-flex tw-justify-end">
                    <Button variant="primary" onClick={this.closeFioPopup}>
                      OK
                    </Button>
                  </div>
                  <Dialog.Close asChild>
                    <button
                      className="tw-absolute tw-right-4 tw-top-4 tw-h-5 tw-w-5 tw-rounded-sm tw-border-0 tw-bg-transparent tw-p-0 tw-opacity-70 tw-transition-opacity hover:tw-opacity-100"
                      onClick={this.closeFioPopup}
                    >
                      ×<span className="tw-sr-only">Закрыть</span>
                    </button>
                  </Dialog.Close>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
          ) : null}
        </Portlet>
      </Layout>
    );
  }

  renderPrefs() {
    const fm = (id: MessageDescriptor["id"]) => this.props.intl.formatMessage({ id });
    const { user } = this.props;
    const preferences = { ...user.preferences, ...this.state.preferences };
    return (
      <div className="form-body">
        <h3>
          <FormattedMessage id="preferences" />
        </h3>

        {/* <h4 style={{marginTop: 32}}><b><FormattedMessage id="preferences.common"/></b></h4> */}

        {/*<h4 style={{marginTop: 32}}><b><FormattedMessage id="preferences.ipr"/></b></h4>
      <div className="form-group">
        <label className="control-label">
          <input type="checkbox" name="preferences.perform_ipr"
            defaultChecked={preferences.perform_ipr}
            onInput={this.updatePreferences}
          />
          &nbsp;<FormattedMessage id="preferences.ipr_enabled"/>
        </label>
      </div>
    */}
        <h4 style={{ marginTop: 32 }}>
          <b>
            <FormattedMessage id="preferences.ipr" />
          </b>
        </h4>
        <FormattedMessage id="preferences.ipr_enabled" />

        <div className="row">
          <div className="radio-list col-md-3" style={{ marginTop: "10px" }}>
            <label htmlFor="preferences.perform_ipr.yes">
              <div className="radio">
                <input
                  type="radio"
                  id="preferences.perform_ipr.yes"
                  name="preferences.perform_ipr"
                  defaultValue={1}
                  onChange={this.updatePreferenceRadio}
                  defaultChecked={[1, true].includes(preferences.perform_ipr)}
                />
              </div>
              <FormattedMessage id="bool.true" />
            </label>

            <label htmlFor="preferences.perform_ipr.no">
              <div className="radio">
                <input
                  type="radio"
                  id="preferences.perform_ipr.no"
                  name="preferences.perform_ipr"
                  defaultValue={0}
                  onChange={this.updatePreferenceRadio}
                  defaultChecked={[0, false].includes(preferences.perform_ipr)}
                />
              </div>
              <FormattedMessage id="bool.false" />
            </label>
          </div>

          <div className="form-group col-md-4">
            <label htmlFor="preferences.ipr_start_step" className="control-label">
              <FormattedMessage id="preferences.ipr_start_step" />
            </label>
            <input
              className="form-control intPositive"
              type="number"
              min="1"
              max={MAX_STAGES}
              step="1"
              disabled={!preferences.perform_ipr}
              defaultValue={preferences.ipr_start_step}
              name="preferences.ipr_start_step"
              id="preferences.ipr_start_step"
              onInput={this.updatePreferences}
            />
          </div>
        </div>

        <h4 style={{ marginTop: 32 }}>
          <b>
            <FormattedMessage id="preferences.attachments" />
          </b>
        </h4>
        <div className="form-group">
          <label htmlFor="preferences.place_attachments_on_step" className="control-label">
            <FormattedMessage id="preferences.attachments_delay" />
          </label>
          <input
            className="form-control intPositive"
            type="number"
            min="1"
            max={MAX_STAGES}
            step="1"
            name="preferences.place_attachments_on_step"
            id="preferences.place_attachments_on_step"
            defaultValue={preferences.place_attachments_on_step}
            onInput={this.updatePreferences}
            style={{ width: "30%" }}
          />
        </div>

        <h4 style={{ marginTop: 32 }}>
          <b>
            <FormattedMessage id="preferences.expansion" />
          </b>
        </h4>
        <FormattedMessage id="preferences.always_expand" />
        <div className="radio-list" style={{ marginTop: "10px" }}>
          <label htmlFor="preferences.always_expand.yes">
            <div className="radio">
              <input
                type="radio"
                id="preferences.always_expand.yes"
                name="preferences.always_expand"
                defaultValue={1}
                onChange={this.updatePreferenceRadio}
                defaultChecked={[1, true].includes(preferences.always_expand)}
              />
            </div>
            <FormattedMessage id="bool.true" />
          </label>
          <label htmlFor="preferences.always_expand.no">
            <div className="radio">
              <input
                type="radio"
                id="preferences.always_expand.no"
                name="preferences.always_expand"
                defaultValue={0}
                onChange={this.updatePreferenceRadio}
                defaultChecked={[0, false].includes(preferences.always_expand)}
              />
            </div>
            <FormattedMessage id="bool.false" />
          </label>
        </div>

        {
          <div>
            <h4 style={{ marginTop: 32 }}>
              <b>
                <FormattedMessage id="preferences.incisor_relationship" />
              </b>
            </h4>
            <div className="row">
              <div className="form-group col-md-4">
                <label htmlFor="preferences.incisor_relationship_overjet" className="control-label">
                  <FormattedMessage id="preferences.incisor_relationship.overjet" />
                </label>
                <input
                  className="form-control floatPositive"
                  type="number"
                  min="0"
                  step="any"
                  name="preferences.incisor_relationship_overjet"
                  id="preferences.incisor_relationship_overjet"
                  defaultValue={preferences.incisor_relationship_overjet}
                  onInput={this.updatePreferences}
                />
              </div>
              <div className="form-group col-md-4">
                <label htmlFor="preferences.incisor_relationship_overbite" className="control-label" style={{ maxWidth: "90%" }}>
                  <FormattedMessage id="preferences.incisor_relationship.overbite" />
                </label>
                <input
                  className="form-control floatPositive"
                  type="number"
                  min="1"
                  step="any"
                  name="preferences.incisor_relationship_overbite"
                  id="preferences.incisor_relationship_overbite"
                  defaultValue={preferences.incisor_relationship_overbite}
                  onInput={this.updatePreferences}
                />
              </div>
              <div className="form-group col-md-4">
                <label htmlFor="preferences.upper_incisor_angulation" className="control-label">
                  <FormattedMessage id="preferences.upper_incisor_angulation" />
                </label>
                <input
                  className="form-control"
                  type="text"
                  name="preferences.upper_incisor_angulation"
                  id="preferences.upper_incisor_angulation"
                  defaultValue={preferences.upper_incisor_angulation}
                  onInput={this.updatePreferences}
                />
              </div>
            </div>

            <h4 style={{ marginTop: 32 }}>
              <b>
                <FormattedMessage id="preferences.overcorrection" />
              </b>
            </h4>
            <div className="row">
              <div className="col-md-4">
                <FormattedMessage id="preferences.deep_bite_intrusion" />
                <div className="radio-list" style={{ marginTop: "10px" }}>
                  <label>
                    <div className="radio">
                      <span>
                        <input
                          type="radio"
                          name="preferences.deep_bite_intrusion"
                          defaultValue={1}
                          onChange={this.updatePreferenceRadio}
                          defaultChecked={[1, true].includes(preferences.deep_bite_intrusion)}
                        />
                      </span>
                    </div>
                    <FormattedMessage id="bool.true" />
                  </label>
                  <label>
                    <div className="radio">
                      <span>
                        <input
                          type="radio"
                          name="preferences.deep_bite_intrusion"
                          defaultValue={0}
                          onChange={this.updatePreferenceRadio}
                          defaultChecked={[0, false].includes(preferences.deep_bite_intrusion)}
                        />
                      </span>
                    </div>
                    <FormattedMessage id="bool.false" />
                  </label>
                </div>
              </div>
              <div className="col-md-4">
                <FormattedMessage id="preferences.incisors_extrusion" />
                <div className="radio-list" style={{ marginTop: "10px" }}>
                  <label>
                    <div className="radio">
                      <span>
                        <input
                          type="radio"
                          name="preferences.incisors_extrusion"
                          defaultValue={1}
                          onChange={this.updatePreferenceRadio}
                          defaultChecked={[1, true].includes(preferences.incisors_extrusion)}
                        />
                      </span>
                    </div>
                    <FormattedMessage id="bool.true" />
                  </label>
                  <label>
                    <div className="radio">
                      <span>
                        <input
                          type="radio"
                          name="preferences.incisors_extrusion"
                          defaultValue={0}
                          onChange={this.updatePreferenceRadio}
                          defaultChecked={[0, false].includes(preferences.incisors_extrusion)}
                        />
                      </span>
                    </div>
                    <FormattedMessage id="bool.false" />
                  </label>
                </div>
              </div>
              <div className="col-md-4">
                <FormattedMessage id="preferences.rotation_over_30dg" />
                <div className="radio-list" style={{ marginTop: "10px" }}>
                  <label>
                    <div className="radio">
                      <span>
                        <input
                          type="radio"
                          name="preferences.rotation_over_30dg"
                          defaultValue={1}
                          onChange={this.updatePreferenceRadio}
                          defaultChecked={[1, true].includes(preferences.rotation_over_30dg)}
                        />
                      </span>
                    </div>
                    <FormattedMessage id="bool.true" />
                  </label>
                  <label>
                    <div className="radio">
                      <span>
                        <input
                          type="radio"
                          name="preferences.rotation_over_30dg"
                          defaultValue={0}
                          onChange={this.updatePreferenceRadio}
                          defaultChecked={[0, false].includes(preferences.rotation_over_30dg)}
                        />
                      </span>
                    </div>
                    <FormattedMessage id="bool.false" />
                  </label>
                </div>
              </div>
            </div>
          </div>
        }

        <h4 style={{ marginTop: 32 }}>
          <label htmlFor="preferences.special_instructions" style={{ fontWeight: "bold", marginBottom: 0 }}>
            <FormattedMessage id="preferences.additional_notes" />
          </label>
        </h4>
        <div className="form-group">
          {/* <label className="control-label"><FormattedMessage id="preferences.additional_notes"/></label> */}
          <textarea
            className="form-control"
            rows="5"
            placeholder={fm("preferences.additional_notes.sub")}
            name="preferences.special_instructions"
            id="preferences.special_instructions"
            defaultValue={preferences.special_instructions}
            onInput={this.updatePreferences}
          />
        </div>
      </div>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(DoctorUpdatePage));
